import classNames from 'classnames';
import React, { FC, useRef, CSSProperties, ChangeEvent, FocusEvent, ReactNode } from 'react';
import styled, { Interpolation } from 'styled-components';

interface TextareaProps {
    name: string;
    value?: string;
    label?: string | ReactNode;
    placeholder?: string;
    error?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    className?: string;
    rows?: number;
    cols?: number;
    containerStyles?: Interpolation<CSSProperties>;
    // eslint-disable-next-line no-unused-vars
    onChange?: (value: string, name: string) => void;
    // eslint-disable-next-line no-unused-vars
    onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void;
}

const TextAreaContainer = styled.div<{
    styles: Interpolation<CSSProperties>;
}>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    &.textarea-element-fullWidth {
        width: 100%;
    }
    ${({ styles }) => styles};
`;

const StyledTextArea = styled.textarea`
    ${({ theme: { colors, typography } }) => `
        background: ${colors.background.secondary.v3};
        border: 1px solid ${colors.border.light};
        &:hover, :focus-within {
            border-color: ${colors.primary};
        }
        &.textarea-element-error {
            border-color: ${colors.alert.red};
        }
        border-radius: 4px;
        padding: 8px 12px;
        color: ${colors.text.primary};
        font-size: ${typography.size.desktop.paragraph.p2};
        font-family: inherit;
        outline: none;
        width: 100%;
        resize: none;
    `}
`;

const StyledLabel = styled.span<{ htmlFor: string }>`
    ${({ theme: { colors, typography } }) => `
        color: ${colors.text.primary};
        font-size: ${typography.size.desktop.paragraph.p3};
        width: 100%;
        width: -moz-available; /* WebKit-based browsers will ignore this. */
        width: -webkit-fill-available; /* Mozilla-based browsers will ignore this. */
        width: fill-available;
        max-width: fit-content;
    `};
`;

const StyledError = styled.span`
    ${({ theme: { colors, typography } }) => `
        color: ${colors.alert.red};
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: calc(${typography.lineHeight[2]} - 1px);
        width: 100%;
        width: -moz-available; /* WebKit-based browsers will ignore this. */
        width: -webkit-fill-available; /* Mozilla-based browsers will ignore this. */
        width: fill-available;
    `};
`;

const TextArea: FC<TextareaProps> = ({
    name,
    value,
    label,
    placeholder,
    containerStyles,
    error,
    disabled,
    fullWidth,
    className,
    rows,
    cols,
    onChange,
    onBlur,
}) => {
    const fieldRef = useRef<HTMLTextAreaElement>(null);

    // focus on textarea field
    const focusField = () => {
        fieldRef && fieldRef.current && fieldRef.current.focus();
    };

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        onChange && onChange(e.target.value, e.target.name);
    };

    return (
        <TextAreaContainer
            className={classNames(
                'textarea-element',
                className,
                fullWidth && 'textarea-element-fullWidth',
            )}
            styles={containerStyles}
        >
            {label && (
                <StyledLabel className={'textarea-label'} onClick={focusField} htmlFor={name}>
                    {label}
                </StyledLabel>
            )}
            <StyledTextArea
                className={classNames('textarea', error && 'textarea-element-error')}
                placeholder={placeholder}
                value={value}
                ref={fieldRef}
                name={name}
                disabled={disabled}
                id={name}
                rows={rows}
                cols={cols}
                onBlur={onBlur}
                onChange={handleChange}
            />
            {error && <StyledError>{error}</StyledError>}
        </TextAreaContainer>
    );
};

export { TextArea };
